import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap } from "@/api/dtos";
import ConfigurationSourceDataBulkEdit from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataBulkEdit";
import ConfigurationSourceDataBulkEditMultipleTypes from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataBulkEditMultipleTypes";
import { ConfigurationSourceDataFormItemDetails, ConfigurationSourceDataFormItemGroupDetails } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataFormDetails";
import { ConfigurationSourceDataItemBulkEditSettings } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataItemForm";
import { ConfigurationSourceDataItemGroupBulkEditSettings } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataItemGroupForm";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useFormVersionConfigurations from "@/hooks/form/configuration/useFormVersionConfigurations";
import useItemConfigurations, { ItemConfiguration, ItemGroupConfiguration, ItemType } from "@/hooks/form/configuration/useItemConfigurations";
import useDebouncedValue from "@/hooks/utility/useDebouncedValue";
import sourceDataFieldTypeToText from "@/utilities/configuration/sourceDataFieldTypeToText";
import sourceDataItemGroupTypeToText from "@/utilities/configuration/sourceDataItemGroupTypeToText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, BoxProps, Button, Chip, Divider, Fade, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip, alpha, lighten, styled } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, Fragment, useCallback, useContext, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

export const ActionButton = styled(Button)(({ theme }) => ({ 
    borderRadius: 0,
    fontSize: 16
}));

export const ActionButtonBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between'
}));

export const ActionBox = styled(Box)(({ theme }) => ({
    top: 0, 
    bottom: 0, 
    height: '48px', 
    display: 'flex', 
    marginRight: theme.spacing(-2)
}));

export const TypeBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minWidth: 135,
    //background: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
    //color: theme.palette.common.white,
    alignSelf: 'stretch',
    margin: theme.spacing(0),
    display: 'flex',
    alignItems: 'center'
}));

export const IndentBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8),
    borderLeft: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)}`
}));

export const IndentDivider = styled(Divider)(({ theme }) => ({
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)
}));

interface IConfigurationSourceDataItemListProps {
    itemData: Array<ItemConfiguration | ItemGroupConfiguration>;
}

const ConfigurationSourceDataItemList: FC<IConfigurationSourceDataItemListProps> = ({
    itemData
}) => {
    return (
        <List disablePadding>
            {
                itemData.map((x, i, a) => {
                    if (x.type === ItemType.ItemGroup) {
                        return (
                            <Fragment
                                key={x.itemGroup.itemGroupName}
                            >
                                <ConfigurationSourceDataFormItemGroupDetails
                                    itemGroupData={x.itemGroup}
                                    divider={(i + 1) < a.length || x.items.length > 0}
                                />
                                <IndentBox>
                                    <ConfigurationSourceDataItemList itemData={x.items} />
                                </IndentBox>
                                <IndentDivider />
                            </Fragment>
                        );
                    }

                    return (
                        <ConfigurationSourceDataFormItemDetails
                            key={x.item.itemName}
                            itemData={x.item}
                            divider={(i + 1) < a.length}
                        />
                    )
                })
            }
        </List>
    )
}

const BulkEditSettings = {
    [ItemType.Item]: ConfigurationSourceDataItemBulkEditSettings,
    [ItemType.ItemGroup]: ConfigurationSourceDataItemGroupBulkEditSettings
}


const BulkEditGetItem = {
    [ItemType.Item]: (data: any) => data.item,
    [ItemType.ItemGroup]: (data: any) => data.itemGroup,
}

const BulkEditUseIndent = {
    [ItemType.Item]: (data: any) => ({ 
        indent: data.indent === true, 
        last: data.last === true 
    }),
    [ItemType.ItemGroup]: (data: any) => ({ 
        indent: data.indent === true, 
        last: data.last === true 
    }),
}

const ConfigurationSourceDataItems: FC = () => {
    const { studyEventOid, caseReportFormOid, caseReportFormVersionOid, itemGroupName } = useParams();
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata } = useContext(MetadataContext);
    const { state: { value: configuration } } = useScopedField<ConfigurationSourceDataMap>('configuration', { value: true });
    const { state: { value: trialConfiguration } } = useScopedField<ConfigurationSourceDataMap>('trialConfiguration', { value: true });


    const {
        state: debouncedConfiguration,
        doNotDebounceNextUpdate: doNoteDebounceNextConfigurationUpdate
    } = useDebouncedValue(configuration, 500);

    const {
        state: debouncedTrialConfiguration,
        doNotDebounceNextUpdate: doNoteDebounceNextTrialConfigurationUpdate
    } = useDebouncedValue(trialConfiguration, 500);

    const onLinkUpdate = useCallback(() => {
        doNoteDebounceNextConfigurationUpdate();
        doNoteDebounceNextTrialConfigurationUpdate();
    }, [doNoteDebounceNextConfigurationUpdate, doNoteDebounceNextTrialConfigurationUpdate])
    
    const itemData = useItemConfigurations(
        metadata,
        debouncedTrialConfiguration ?? undefined,
        debouncedConfiguration ?? undefined,
        trial,
        debouncedConfiguration?.configuration?.id,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName
    );

    const flatItemData = useMemo(() => {
        return itemData?.flatMap(x => {

            if (x.type === ItemType.ItemGroup && x.items) {
                return [x, ...x.items.map((y, i, a) => { 
                    (y as any).indent = true;
                    (y as any).last = (i + 1) === a.length
                    return y
                })]
            }

            return [x];
        }) ?? []
    }, [itemData])

    const [bulkEdit, setBulkEdit] = useState(false);

    const onBulkEditCancel = useCallback(() => {
        setBulkEdit(false);
    }, [setBulkEdit])
    
    if (!itemData || itemData.length === 0) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={AppSettings.icons.alertWarning} />}
                severity="warning"
            >
                <AlertTitle>No Items</AlertTitle>
                No items were found.
            </Alert> 
        )
    }
    
    
    if (bulkEdit) {
        return (
            <ConfigurationSourceDataBulkEditMultipleTypes
                data={flatItemData}
                settings={BulkEditSettings as any}
                getItem={BulkEditGetItem}
                useIndent={BulkEditUseIndent}
                onLinkUpdate={onLinkUpdate}
                onCancel={onBulkEditCancel}
            />
        )
    }
    

    return (
        <>
            <Box
                sx={{
                    my: 2,
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center'
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    sx={{
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                        minHeight: 52,
                        ml: 'auto'
                    }}
                    onClick={() => setBulkEdit(true)}
                >
                    Bulk Edit
                </Button>
            </Box>
            <Paper>
                <ConfigurationSourceDataItemList itemData={itemData} />
            </Paper>
        </>
    )
};

export default ConfigurationSourceDataItems;